import styled, { css, keyframes } from 'styled-components';

// CONSTANTS
import { theme } from '../../constants/theme';
import { responsiveEndPoints } from '../../constants/responsive-endpoints';

// ENUMS
import { Themes } from '../../enums/themes';

//INTERFACES
import { IMenuItemProps } from '../../interfaces/IMenuItem.interface';
import { ISideBar } from '../../interfaces/SideBar.interface';

const backgroundColor = {
  [Themes.DARK]: theme.colors.primary.black,
  [Themes.LIGHT]: theme.colors.primary.white,
  [Themes.PRIMARY]: theme.colors.primary.bariBlue
};

export const onEnter = keyframes`
	0% {
    transform: translateX(-313px);
	}
	100% {
    transform: translateX(0);
	}
`;

export const onLeave = keyframes`
  100% {
    transform: translateX(-313px);
  }
	0% {
    transform: translateX(0);
	}
`;

export const Container = styled.div<ISideBar>`
  display: flex;
  z-index: 10002;
  position: ${({ fixedPosition }) => (fixedPosition ? 'fixed' : 'relative')};
  flex-direction: column;
  padding: 40px 20px 40px 24px;
  width: 300px;
  height: 100vh;
  box-sizing: border-box;
  justify-content: space-between;
  font-family: 'Clan Pro';
  background-color: ${({ themeProp }) => backgroundColor[themeProp]};
  overflow-y: scroll;

  & {
    scrollbar-width: thin;
    scrollbar-color: ${theme.colors.primary.white} ${theme.colors.primary.black};
  }

  /* Works on Chrome, Edge, and Safari */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${theme.colors.primary.black};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${theme.colors.primary.white}50;
    border-radius: 20px;
    border: 2px solid ${theme.colors.primary.black};
  }

  @media (max-width: ${responsiveEndPoints.responsive}px) {
    width: 100px;
    padding: 38px 0px;
    align-items: center;
  }
`;

export const SubSideBarContainer = styled.div<{ isLeaved: boolean }>`
  position: fixed;
  display: flex;
  opacity: 1;
  z-index: 10001;
  animation: ${({ isLeaved }) => (isLeaved ? onLeave : onEnter)} 0.6s ease-in;
  width: 313px;
  height: 100vh;
  top: 0;
  left: 300px;
  background-color: ${theme.colors.secondary.black100};
  font-family: 'Clan Pro';
  padding: 38px;
  align-items: center;

  @media (max-width: ${responsiveEndPoints.responsive}px) {
    left: 100px;
  }
`;

export const SubSideBarItemContainer = styled.div`
  display: block;
  width: 100%;
`;

export const Content = styled.div`
  display: flex;
  justify-content: center;
`;

export const WrapperLogo = styled.div<ISideBar>`
  padding: 0 20px;
  margin-bottom: 78px;
  ${({ themeProp }) =>
    themeProp == Themes.LIGHT &&
    css`
      svg {
        rect {
          fill: ${theme.colors.primary.black};
        }
        path {
          &:first-child {
            fill: ${theme.colors.primary.black};
          }
          &:last-child {
            fill: ${theme.colors.primary.bariBlue};
          }
        }
      }
    `}

  ${({ themeProp }) =>
    themeProp == Themes.PRIMARY &&
    css`
      svg {
        path {
          &:last-child {
            fill: ${theme.colors.primary.black};
          }
        }
      }
    `}

  @media (max-width: ${responsiveEndPoints.responsive}px) {
    padding: 0;
    text-align: center;
  }
`;

export const Menu = styled.nav`
  display: block;
  padding: 0;
  margin: 0;
  list-style: none;
  @media (max-width: ${responsiveEndPoints.responsive}px) {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }
`;

export const Logout = styled.div`
  position: relative;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  max-width: 100%;
  padding: 0 16px;
  margin: 12px 0;
  cursor: pointer;
  border-radius: 8px;

  &:hover {
    background: ${theme.colors.secondary.gray200}60;
    > label {
      color: ${theme.colors.primary.white};
    }
    > div > svg {
      path {
        fill: ${theme.colors.primary.white};
      }
    }
  }

  &:before {
    content: '';

    position: absolute;
    top: -6px;
    left: 0;

    width: 100%;
    height: 1px;
    background-color: ${theme.colors.primary.transparentBg}50;
  }
`;

export const LogoutIcon = styled.div<ISideBar>`
  cursor: pointer;
  position: absolute;
  top: 12px;
  right: 32px;
`;

export const LogoutLabel = styled.label<ISideBar>`
  display: block;
  font-size: 12.6px;
  line-height: 48px;
  letter-spacing: 0.02em;
  font-weight: ${theme.weights.semibold};
  cursor: pointer;

  ${({ themeProp }) =>
    themeProp == Themes.DARK &&
    css`
      color: ${theme.colors.secondary.gray200};
    `}
`;

export const ItemSubMenuContainer = styled.div<IMenuItemProps>`
  cursor: pointer;

  > a {
    cursor: pointer;
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0 16px;
    margin-bottom: 8px;
    border-radius: 8px;
    text-decoration: none;
    font-size: 12.6px;
    line-height: 48px;
    letter-spacing: 0.04em;
    font-weight: ${theme.weights.semibold};
    color: ${theme.colors.primary.white};

    > label {
      cursor: pointer;
    }

    &:hover,
    &:focus {
      background: ${theme.colors.secondary.gray200}60;
      color: ${theme.colors.primary.white};
    }

    > svg > path {
      fill: ${theme.colors.primary.white};
    }

    ${({ active }) =>
      active &&
      css`
        background: ${theme.colors.primary.white};
        color: ${theme.colors.primary.black};

        > svg > path {
          fill: ${theme.colors.primary.black};
        }
      `}
  }
`;

export const ItemMenuContainer = styled.div<IMenuItemProps>`
  position: relative;
  cursor: pointer;
  > a {
    display: flex;
    align-items: center;
    padding: 0 16px;
    margin-bottom: 8px;
    border-radius: 8px;
    text-decoration: none;
    font-size: 12.6px;
    line-height: 48px;
    letter-spacing: 0.04em;
    font-weight: ${theme.weights.semibold};

    @media (max-width: 1360px) {
      width: 60px;
      height: 60px;
      display: flex;
      align-items: center;
      justify-content: center;
      box-sizing: border-box;
    }

    ${({ active }) =>
      !active &&
      css`
        color: ${theme.colors.secondary.gray200};
        font-weight: ${theme.weights.bold};
        &:hover,
        &:focus {
          background: ${theme.colors.secondary.gray200}60;
          color: ${theme.colors.primary.white};
          svg .fill {
            fill: ${theme.colors.primary.white} !important;
          }
          svg path {
            fill: ${theme.colors.secondary.gray200}60;
          }
          svg .stroke {
            stroke: ${theme.colors.primary.white} !important;
            stroke-width: 2;
          }
          svg rect {
            stroke: ${theme.colors.primary.white};
          }
        }
      `}

    ${({ active }) =>
      active &&
      css`
        background: ${theme.colors.primary.white};
        color: ${theme.colors.primary.black};
        & svg .fill {
          fill: #101010 !important;
        }
        & svg .stroke {
          stroke: #101010 !important;
          stroke-width: 2;
        }
        & .checked {
          display: initial;
        }
      `}

    &:focus {
      &:before {
        opacity: 1;
      }
    }

    @media (min-width: 1361px) {
      &:before {
        content: '';
        opacity: 0;
        position: absolute;
        top: 21px;
        right: 16px;
        display: block;
        width: 6px;
        height: 6px;
        border-radius: 50%;
        background: ${theme.colors.primary.white};
      }
    }

    ${({ active }) =>
      active &&
      css`
        &:before {
          background: ${theme.colors.primary.black};
          opacity: 1;
        }
      `}
  }

  > a > svg {
    position: absolute;
    top: 12px;
    right: 30px;
    @media (max-width: ${responsiveEndPoints.responsive}px) {
      display: flex;
      position: initial;
    }
    & .fill {
      fill: ${theme.colors.secondary.gray200};
    }
    & .stroke {
      stroke: ${theme.colors.secondary.gray200};
      stroke-width: 2;
    }
  }

  > a > label {
    @media (max-width: ${responsiveEndPoints.responsive}px) {
      display: none;
    }
  }
`;
